import React from 'react'
import moment from 'moment-timezone'
import { getVisitsVisitIdV2, useGetVisitsWaitingroomV2 } from 'api/generated/visit-controller-v2/visit-controller-v2'
import { type WaitingRoomV2VisitListItemDoctorApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { useGetProfile } from 'api/generated/profile/profile'
import { parseTimeZone } from 'utils/common'
import { VisitMethods } from 'constants/VisitMethods'

export type JustChangeKind = 'just-scheduled' | 'just-rescheduled'
export type DismissedKind = 'cancelled' | 'rescheduled'

export type JustChangeTag = {
  kind: JustChangeKind
  addedAt: number
  seenAt?: number | undefined
  fadeCompleted?: boolean
}

const FADE_DURATION_MS = 8_000

export type DismissedVisit = {
  key: string
  visit: WaitingRoomV2VisitListItemDoctorApiOutContract
  kind: DismissedKind
  newDateTime?: string
  dismissedAt: number
}

export type VisitLists = {
  fullList: WaitingRoomV2VisitListItemDoctorApiOutContract[]
  scheduleList: WaitingRoomV2VisitListItemDoctorApiOutContract[]
  pendingList: WaitingRoomV2VisitListItemDoctorApiOutContract[]
  onlineVisitsList: WaitingRoomV2VisitListItemDoctorApiOutContract[]
  nextVisit: WaitingRoomV2VisitListItemDoctorApiOutContract | undefined
  justChangeTags: Record<string, JustChangeTag>
  dismissedVisits: DismissedVisit[]
  dismissVisit: (key: string) => void
  /** True only during the initial waiting-room fetch (no data yet); false on MQTT-triggered background refetches. */
  isLoading: boolean
}

const DISMISSED_KEY_SUFFIX = ':dismissed'
const BROADCAST_CHANNEL_NAME = 'qmd-just-change-seen'
const BROADCAST_RECENCY_MS = 30_000

type SeenBroadcast = {
  type: 'seen'
  visitId: string
  kind: JustChangeKind
  seenAt: number
}

export const useVisitLists = ({ isActive = true }: { isActive?: boolean } = {}): VisitLists => {
  const profile = useGetProfile({ query: { enabled: false } }).data
  const timezone = parseTimeZone(profile?.TimeZone)

  const { data, isLoading } = useGetVisitsWaitingroomV2({ timezone })

  const fullList = React.useMemo(() => data ?? [], [data])

  const isAsync = React.useCallback(
    (visit: WaitingRoomV2VisitListItemDoctorApiOutContract) => visit.Method === VisitMethods.ASYNCHRONOUS,
    [],
  )

  const onlineVisitsList = React.useMemo(() => (fullList ?? []).filter(isAsync), [fullList, isAsync])

  const isBeforeToday = React.useCallback(
    (dateTime?: string) => {
      if (!dateTime) return false
      return moment.tz(dateTime, timezone).isBefore(moment.tz(timezone).startOf('day'))
    },
    [timezone],
  )

  const isToday = React.useCallback(
    (dateTime?: string) => {
      if (!dateTime) return false
      return moment.tz(dateTime, timezone).isSame(moment.tz(timezone), 'day')
    },
    [timezone],
  )

  const pendingList = React.useMemo(
    () =>
      (fullList ?? []).filter(
        (visit) => !isAsync(visit) && (visit.Status === 'pending' || isBeforeToday(visit.DateTime)),
      ),
    [fullList, isBeforeToday, isAsync],
  )

  const previousListRef = React.useRef<WaitingRoomV2VisitListItemDoctorApiOutContract[] | undefined>(undefined)
  const [tags, setTags] = React.useState<Record<string, JustChangeTag>>({})
  const [dismissed, setDismissed] = React.useState<Record<string, DismissedVisit>>({})

  const nextVisit = React.useMemo(() => {
    const candidates = (fullList ?? []).filter((visit) => {
      if (isAsync(visit)) return false
      if (visit.Status === 'pending') return false
      if (!isToday(visit.DateTime)) return false
      if (visit.VisitId && dismissed[visit.VisitId]) return false
      return true
    })
    candidates.sort((a, b) => new Date(a.DateTime).getTime() - new Date(b.DateTime).getTime())
    return candidates[0]
  }, [fullList, isToday, dismissed, isAsync])

  const scheduleList = React.useMemo(
    () =>
      (fullList ?? []).filter(
        (visit) =>
          !isAsync(visit) &&
          visit.Status !== 'pending' &&
          !isBeforeToday(visit.DateTime) &&
          visit.VisitId !== nextVisit?.VisitId,
      ),
    [fullList, isBeforeToday, nextVisit, isAsync],
  )
  const inFlightFetches = React.useRef<Set<string>>(new Set())
  const recentSeenBroadcasts = React.useRef<Map<string, SeenBroadcast & { receivedAt: number }>>(new Map())
  const channelRef = React.useRef<BroadcastChannel | null>(null)

  React.useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return
    const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME)
    channelRef.current = channel
    const onMessage = (event: MessageEvent<SeenBroadcast>) => {
      const { data: payload } = event
      if (!payload || payload.type !== 'seen') return
      const now = Date.now()
      recentSeenBroadcasts.current.forEach((entry, key) => {
        if (now - entry.receivedAt > BROADCAST_RECENCY_MS) {
          recentSeenBroadcasts.current.delete(key)
        }
      })
      recentSeenBroadcasts.current.set(`${payload.visitId}:${payload.kind}`, {
        ...payload,
        receivedAt: now,
      })
      setTags((prev) => {
        const tag = prev[payload.visitId]
        if (!tag || tag.kind !== payload.kind || tag.seenAt !== undefined) return prev
        return { ...prev, [payload.visitId]: { ...tag, seenAt: payload.seenAt } }
      })
    }
    channel.addEventListener('message', onMessage)
    return () => {
      channel.removeEventListener('message', onMessage)
      channel.close()
      channelRef.current = null
    }
  }, [])

  const consumeRecentBroadcast = React.useCallback((visitId: string, kind: JustChangeKind): number | undefined => {
    const key = `${visitId}:${kind}`
    const entry = recentSeenBroadcasts.current.get(key)
    if (!entry) return undefined
    recentSeenBroadcasts.current.delete(key)
    if (Date.now() - entry.receivedAt > BROADCAST_RECENCY_MS) return undefined
    return entry.seenAt
  }, [])

  React.useEffect(() => {
    if (!isActive) return
    const unseen = Object.entries(tags).filter(([, tag]) => tag.seenAt === undefined)
    if (unseen.length === 0) return
    const now = Date.now()
    setTags((prev) => {
      let changed = false
      const next = { ...prev }
      unseen.forEach(([visitId]) => {
        const tag = prev[visitId]
        if (!tag || tag.seenAt !== undefined) return
        next[visitId] = { ...tag, seenAt: now }
        changed = true
      })
      return changed ? next : prev
    })
    const channel = channelRef.current
    if (channel) {
      unseen.forEach(([visitId, tag]) => {
        channel.postMessage({ type: 'seen', visitId, kind: tag.kind, seenAt: now })
      })
    }
  }, [isActive, tags])

  React.useEffect(() => {
    const seenAndUnfaded = Object.entries(tags).filter(([, tag]) => tag.seenAt !== undefined && !tag.fadeCompleted)
    if (seenAndUnfaded.length === 0) return
    const timeouts = seenAndUnfaded.map(([visitId, tag]) => {
      const remaining = Math.max(0, FADE_DURATION_MS - (Date.now() - (tag.seenAt ?? 0)))
      return window.setTimeout(() => {
        setTags((prev) => {
          const t = prev[visitId]
          if (!t || t.fadeCompleted) return prev
          return { ...prev, [visitId]: { ...t, fadeCompleted: true } }
        })
      }, remaining)
    })
    return () => {
      timeouts.forEach((t) => window.clearTimeout(t))
    }
  }, [tags])

  React.useEffect(() => {
    if (!data) return

    const previousList = previousListRef.current
    const now = Date.now()
    const currentVisitIds = new Set(data.map((v) => v.VisitId).filter((id): id is string => Boolean(id)))

    if (previousList) {
      const previousById = new Map(
        previousList.filter((v): v is typeof v & { VisitId: string } => Boolean(v.VisitId)).map((v) => [v.VisitId, v]),
      )
      setTags((prev) => {
        const next = { ...prev }
        let changed = false
        data.forEach((visit) => {
          const visitId = visit.VisitId
          if (!visitId) return
          const previousVisit = previousById.get(visitId)
          let kind: JustChangeKind | undefined
          if (!previousVisit) kind = 'just-scheduled'
          else if (previousVisit.DateTime !== visit.DateTime) kind = 'just-rescheduled'
          if (!kind) return
          const broadcastSeenAt = consumeRecentBroadcast(visitId, kind)
          next[visitId] = { kind, addedAt: now, seenAt: broadcastSeenAt }
          changed = true
        })
        return changed ? next : prev
      })

      setDismissed((prev) => {
        const next = { ...prev }
        let changed = false
        data.forEach((visit) => {
          const visitId = visit.VisitId
          if (!visitId) return
          const previousVisit = previousById.get(visitId)
          if (previousVisit && previousVisit.DateTime !== visit.DateTime) {
            const key = `${visitId}${DISMISSED_KEY_SUFFIX}`
            next[key] = {
              key,
              visit: previousVisit,
              kind: 'rescheduled',
              newDateTime: visit.DateTime,
              dismissedAt: now,
            }
            changed = true
          }
        })
        return changed ? next : prev
      })
    }

    setDismissed((prev) => {
      let changed = false
      const next: Record<string, DismissedVisit> = { ...prev }
      Object.keys(prev).forEach((key) => {
        if (key.endsWith(DISMISSED_KEY_SUFFIX)) return
        if (currentVisitIds.has(key)) {
          delete next[key]
          changed = true
        }
      })
      return changed ? next : prev
    })

    if (previousList) {
      const disappeared = previousList.filter((visit) => visit.VisitId && !currentVisitIds.has(visit.VisitId))
      const todayEnd = moment.tz(timezone).endOf('day')
      disappeared.forEach((visit) => {
        const visitId = visit.VisitId
        if (!visitId) return
        if (inFlightFetches.current.has(visitId)) return
        inFlightFetches.current.add(visitId)
        getVisitsVisitIdV2(visitId, { includeMostRecentMatchingVisit: true })
          .then((info) => {
            const latest = previousListRef.current
            if (latest?.some((v) => v.VisitId === visitId)) return
            const status = info?.Status?.toLowerCase() ?? ''
            const newDateTime = info?.FromDateTime
            if (status.startsWith('cancelled') || status.startsWith('canceled')) {
              setDismissed((prev) => ({
                ...prev,
                [visitId]: { key: visitId, visit, kind: 'cancelled', dismissedAt: Date.now() },
              }))
            } else if (newDateTime && moment.tz(newDateTime, timezone).isAfter(todayEnd)) {
              setDismissed((prev) => ({
                ...prev,
                [visitId]: {
                  key: visitId,
                  visit,
                  kind: 'rescheduled',
                  newDateTime,
                  dismissedAt: Date.now(),
                },
              }))
            }
          })
          .catch(() => {})
          .finally(() => {
            inFlightFetches.current.delete(visitId)
          })
      })
    }

    previousListRef.current = data
  }, [data, timezone, consumeRecentBroadcast])

  const dismissedVisits = React.useMemo<DismissedVisit[]>(
    () => Object.values(dismissed).filter((d) => !isBeforeToday(d.visit.DateTime)),
    [dismissed, isBeforeToday],
  )

  const dismissVisit = React.useCallback((key: string) => {
    setDismissed((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  return {
    fullList,
    scheduleList,
    pendingList,
    onlineVisitsList,
    nextVisit,
    justChangeTags: tags,
    dismissedVisits,
    dismissVisit,
    isLoading,
  }
}
